'use client';

import React, { useState } from 'react';

import { Heading, Body } from '@depop/web-thrift-ui/Typography';
import { ArrowDownIcon } from '@depop/web-ui-kit/Icons/ArrowDownIcon';
import { ArrowTopIcon } from '@depop/web-ui-kit/Icons/ArrowTopIcon';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import {
  BubbleNavigation,
  type BubbleItem as BubbleNavigationTile,
} from '../../explore/components/BubbleNavigation';
import { PopularBrands } from '../PopularBrands';
import { PopularCategories } from '../PopularCategories';

import styles from './styles.module.css';

import { ButtonMinimal } from '@/components/ButtonMinimal';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { getTranslationFromPrefix } from '@/modules/localisation/helper';
import { useProductsSearchWithFallback } from '@/modules/search/useProductsSearchWithFallback';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { formatNumberWithMetricPrefix } from '@/modules/text/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export type Props = {
  title: string;
  description?: string;
  bubbles?: BubbleNavigationTile[];
};

function Header({ title, description, bubbles }: Props) {
  const t = useTranslations('search');
  const { language } = useCurrentLanguage();
  const params = useSearchParams();
  const [isDescriptionToggled, setIsDescriptionToggled] = useState(false);
  const hasDescription = !!description;
  const { data, isFetching } = useProductsSearchWithFallback(
    params.get('q') || ''
  );
  const {
    searchFilters: { brands, productTypes },
  } = useSearchFilters();
  const [, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });

  const count = data?.pages[0]?.data?.meta.total_count;

  function handleToggleDescription() {
    setIsDescriptionToggled((isToggled) => !isToggled);
  }

  const translatedTitle = getTranslationFromPrefix(title, t, 'Header');

  const showPopularCategories = !productTypes && !bubbles;
  const showPopularBrands =
    !showPopularCategories && !brands?.length && !bubbles;

  function handleBubbleClick(tile: BubbleNavigationTile, index: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      itemClicked: tile.label,
      itemPosition: index,
      moduleName: 'subcategory_bubbles',
      schemaVersion: '3.0',
    });
  }

  return (
    <div
      className={clsx(styles.container, {
        [styles.borderBottom]: hasDescription,
      })}
    >
      <div
        className={clsx(styles.heading, {
          [styles.marginBottom]: isDescriptionToggled,
        })}
      >
        <div className={styles.titleRow}>
          <Heading variant="lg">{translatedTitle}</Heading>
          {description && (
            <ButtonMinimal onClick={handleToggleDescription}>
              {isDescriptionToggled ? (
                <ArrowTopIcon size={20} aria-label={t('HideDescription')} />
              ) : (
                <ArrowDownIcon size={20} aria-label={t('ShowDescription')} />
              )}
            </ButtonMinimal>
          )}
        </div>
        <Body variant="sm-regular">
          (
          {isFetching
            ? t('LoadingResultsV2')
            : t('LandingPageTotalItems', {
                totalCount: formatNumberWithMetricPrefix(count || 0, language),
              })}
          )
        </Body>
      </div>
      {description && (
        <Body
          variant="sm-regular"
          className={clsx(styles.description, {
            [styles.hidden]: !isDescriptionToggled,
          })}
        >
          {description}
        </Body>
      )}
      {bubbles && (
        <BubbleNavigation items={bubbles} onBubbleClick={handleBubbleClick} />
      )}
      {showPopularCategories && <PopularCategories />}
      {showPopularBrands && <PopularBrands />}
    </div>
  );
}

export { Header };
